import { Children, createElement, type ReactNode } from "react"

export type GetChildByTypeConfig = {
	customTypeKey?: string
	prioritized?: boolean
}

/* oxlint-disable typescript/prefer-nullish-coalescing */
const typeOfComponent = (component: any, customTypeKey = "__TYPE"): unknown =>
	(component?.props && component.props[customTypeKey]) ||
	(component?.type && component.type[customTypeKey]) ||
	(typeof component?.type === "string" && component.type) ||
	(component?.type &&
		typeof component.type === "symbol" &&
		component.type.toString() === "Symbol(react.fragment)" &&
		"react.fragment") ||
	(typeof component?.type === "function" && component.type) ||
	(typeof component?.type === "object" &&
		component.type.$$typeof?.toString() === "Symbol(react.forward_ref)" &&
		"react.forward_ref") ||
	(typeof component === "string" && "string") ||
	(typeof component === "function" && "function") ||
	undefined
/* oxlint-enable typescript/prefer-nullish-coalescing */

const processTypes = (types: unknown[]): unknown[] =>
	types.map((x) => {
		switch (typeof x) {
			case "string":
				return x
			case "function":
				return typeOfComponent(createElement(x as never))
			case "object":
			default: {
				if (x == null) return x
				const component = createElement(x as never)
				const type = typeOfComponent(component)
				if (type === "react.forward_ref") {
					return typeOfComponent(
						(component.type as any).render(
							component.props,
							(component as any).ref,
						),
					)
				}
				return typeOfComponent(x)
			}
		}
	})

export function getChildByType<T = ReactNode, TC = unknown>(
	children: T,
	types: TC | TC[],
	{
		customTypeKey = "__TYPE",
		prioritized = false,
	}: GetChildByTypeConfig = {},
): T {
	const _types = processTypes(Array.isArray(types) ? types : [types])
	const matches = Children.toArray(children as ReactNode).filter(
		(child) => _types.indexOf(typeOfComponent(child, customTypeKey)) !== -1,
	)

	if (prioritized) {
		for (const type of _types) {
			const match = matches.find(
				(x) => typeOfComponent(x, customTypeKey) === type,
			)
			if (match) return match as T
		}
	}

	return matches[0] as T
}
